/**
 * Interview modes are how a candidate is assessed — not the topic (Python vs HR)
 * and not the intent (practice vs live client interview).
 *
 * Families
 * - Conversation: two-way presence (voice / video)
 * - Knowledge: timed answers scored immediately (written / MCQ / aptitude)
 * - Craft: role-specific drills (coding / typing)
 */

import { preferredCodingLanguage, codingLanguageLabel } from './codingLanguages';

export { preferredCodingLanguage, CODING_LANGUAGES, normalizeCodingLanguage, codingLanguageLabel } from './codingLanguages';

export const MODE_FAMILIES = [
  {
    id: 'conversation',
    title: 'Conversation',
    subtitle: 'Voice or video. Choose 15, 30, or 45 minutes; the whole take is recorded and chunk-analyzed.',
  },
  {
    id: 'knowledge',
    title: 'Knowledge and judgment',
    subtitle: 'Timed, scored screens. Use these before a live round to find gaps fast.',
  },
  {
    id: 'craft',
    title: 'Skill drills',
    subtitle: 'Craft tests hiring teams still run for engineering, ops, support, and BPO roles.',
  },
];

export const INTERVIEW_MODES = [
  {
    id: 'voice',
    family: 'conversation',
    title: 'Voice call',
    icon: 'FiPhone',
    duration: '15 / 30 / 45 min',
    scoring: 'OpenAI chunk analysis; length fallback',
    description:
      'Audio-only interview. Pick 15, 30, or 45 minutes. The whole call is recorded; chunks are analyzed with OpenAI and the full take is sent to WeSenseU over HTTP.',
    features: [
      'Microphone only — no camera',
      'Full-session recording at 15, 30, or 45 minutes',
      '20-second chunks analyzed with OpenAI',
      'Full audio submitted to WeSenseU (HTTP, not a shared DB)',
    ],
    when: 'Screening, HR, sales, support, and any first-round phone interview.',
  },
  {
    id: 'video',
    family: 'conversation',
    title: 'Video call',
    icon: 'FiVideo',
    duration: '15 / 30 / 45 min',
    scoring: 'OpenAI chunk analysis; length fallback',
    description:
      'Camera and microphone. Pick 15, 30, or 45 minutes. The whole call is recorded; chunks go to OpenAI and the assembled video is submitted to WeSenseU over HTTP.',
    features: [
      'Camera + microphone',
      'Full-session recording at 15, 30, or 45 minutes',
      '20-second chunks analyzed with OpenAI',
      'Full video submitted to WeSenseU (HTTP, not a shared DB)',
    ],
    when: 'Manager rounds, client interviews, campus placement video screens.',
  },
  {
    id: 'written',
    family: 'knowledge',
    title: 'Written test',
    icon: 'FiEdit3',
    duration: '15 min',
    scoring: 'Coverage vs sample answer',
    description:
      'Long-form answers. Used for case notes, emails, policy write-ups, and take-home judgment. Rubric is the sample answer from your extracted Q&A.',
    features: [
      'Two open questions',
      'Minimum answer length',
      'Scored against a sample answer',
      'No code editor required',
    ],
    when: 'Analyst, HR, ops, consulting, and any JD that asks for written communication.',
  },
  {
    id: 'mcq',
    family: 'knowledge',
    title: 'MCQ test',
    icon: 'FiCheckSquare',
    duration: '8 min',
    scoring: 'Correct / total',
    description:
      'Short knowledge check from the skills on your resume and job description. Fast signal: do you recognise the stack, or only list it?',
    features: [
      'Eight questions',
      'One correct option',
      'Built from your extracted skills',
      'Instant score',
    ],
    when: 'Tech and domain screens before a live round. Not a substitute for coding or conversation.',
  },
  {
    id: 'aptitude',
    family: 'knowledge',
    title: 'Aptitude and reasoning',
    icon: 'FiCpu',
    duration: '10 min',
    scoring: 'Correct / total, timed',
    description:
      'Numerical, logical, and verbal reasoning. Role-agnostic. Campus and volume hiring still use this as a first filter.',
    features: [
      'Mixed numerical, logic, verbal',
      'Timed overall',
      'No domain knowledge required',
      'Same bar for every candidate',
    ],
    when: 'Campus drives, graduate programmes, and consultancies that screen at volume.',
  },
  {
    id: 'coding',
    family: 'craft',
    title: 'Coding challenge',
    icon: 'FiCode',
    duration: '20 min',
    scoring: 'Tests passed',
    description:
      'Problem, editor, test cases. Use when the job needs you to write working code, not only talk about it. Pick the language the JD names.',
    features: [
      'Problem statement + editor',
      'Automated test cases',
      'Language choice (Python, JS, Java, and more)',
      'Score from passing tests',
    ],
    when: 'Software, data, and automation roles. Skip if the JD has no programming.',
  },
  {
    id: 'typing',
    family: 'craft',
    title: 'Typing speed test',
    icon: 'FiType',
    duration: '2 min',
    scoring: 'WPM and accuracy',
    description:
      'Speed and accuracy on a passage in English or an Indian language. Still required for data entry, support, ops, and many BPO screens.',
    features: [
      'Live WPM',
      'Accuracy and errors',
      'English, Hindi, Tamil, and more',
      'Comparable to a 40 WPM baseline',
    ],
    when: 'Support, ops, transcription, and any JD that mentions typing speed.',
  },
];

export const MODE_PUBLIC_LEAD =
  'Hiring tests how you communicate, what you know, and what you can do. EnterWeU has seven interview modes in those three families — not a single video round, and not leftover typing-or-coding drills.';

export const MODE_NAMES_SHORT =
  'Voice call, Video call, Written test, MCQ test, Aptitude and reasoning, Coding challenge, and Typing speed test';

export const getMode = (id) => INTERVIEW_MODES.find((row) => row.id === id);

export const modesInFamily = (familyId) =>
  INTERVIEW_MODES.filter((row) => row.family === familyId);

const SKILL_USE = {
  python: 'Building backends, automation, and data tools',
  django: 'Building server-rendered and API web apps in Python',
  javascript: 'Adding behaviour to web pages and apps',
  react: 'Building interactive user interfaces',
  docker: 'Packaging software so it runs the same everywhere',
  kubernetes: 'Orchestrating containers across machines',
  aws: 'Running infrastructure on Amazon Web Services',
  sql: 'Querying and shaping relational data',
  postgresql: 'Storing and querying relational data',
  redis: 'Caching and fast in-memory data',
  java: 'Building long-lived backend services',
  git: 'Tracking changes in source code',
};

const ADJACENT_WRONG = {
  python: [
    'Compiling SwiftUI view trees for iOS',
    'Authoring print production CSS for magazine pagination',
    'Configuring Windows Group Policy objects',
  ],
  django: [
    'Training transformer weights from scratch',
    'Writing FPGA netlists in Verilog',
    'Operating a statutory payroll filing desk',
  ],
  javascript: [
    'Designing printed circuit boards',
    'Administering SAP FI/CO ledgers',
    'Tuning analog radio transmitters',
  ],
  react: [
    'Provisioning bare-metal hypervisors',
    'Running GST returns as a compliance clerk',
    'Editing 35mm film stock',
  ],
  docker: [
    'Drafting employment contracts',
    'Color-correcting cinema footage',
    'Filing trademark applications',
  ],
  kubernetes: [
    'Retail POS cash reconciliation',
    'Writing newspaper classifieds',
    'Tuning piano temperament',
  ],
  aws: [
    'Physical warehouse forklift routes',
    'Hand-drawing topographic maps',
    'Operating a letterpress shop',
  ],
  sql: [
    'Typesetting paperback interiors',
    'Mixing live concert audio',
    'Drafting architectural elevations',
  ],
  postgresql: [
    'Animating stop-motion puppets',
    'Issuing municipal parking permits',
    'Hand-weaving textile samples',
  ],
  redis: [
    'Cataloguing museum ceramics',
    'Fitting residential HVAC ducts',
    'Proofreading legal statutes',
  ],
  java: [
    'Layout of bridal magazines',
    'Seasonal crop irrigation schedules',
    'Antique furniture restoration',
  ],
  git: [
    'Retail shelf-planogram design',
    'Broadcast TV continuity scripts',
    'Pastry lamination techniques',
  ],
};

const GENERIC_DISTRACTORS = [
  'Authoring static marketing HTML with no application runtime',
  'Administering end-user spreadsheet macros as the system of record',
  'Operating a point-of-sale till with no software delivery',
];

const distractorsFor = (name) => {
  const key = String(name || '').toLowerCase();
  return ADJACENT_WRONG[key] || GENERIC_DISTRACTORS;
};

const rotate = (items, offset) => {
  const copy = [...items];
  const n = copy.length ? offset % copy.length : 0;
  return copy.slice(n).concat(copy.slice(0, n));
};

export const buildMcq = (skills = []) => {
  const names = (skills.length ? skills : ['Python', 'SQL', 'Communication', 'Problem solving'])
    .map((row) => (typeof row === 'string' ? row : row.name))
    .filter(Boolean)
    .slice(0, 8);
  while (names.length < 8) {
    names.push(['Git', 'Linux', 'APIs', 'Testing', 'Excel', 'Stakeholder management'][names.length % 6]);
  }
  return names.map((name, index) => {
    const key = String(name).toLowerCase();
    const correct = SKILL_USE[key] || `Applying ${name} to production work in this role`;
    const options = rotate([correct, ...distractorsFor(name).slice(0, 3)], index);
    const answer = options.findIndex((text) => text === correct);
    return {
      id: `mcq-${index}`,
      prompt: `Which option best describes how hiring teams use ${name}?`,
      options,
      answer: answer < 0 ? 0 : answer,
      skill: name,
    };
  });
};

export const APTITUDE_BANK = [
  {
    id: 'apt-1',
    kind: 'numerical',
    prompt: 'A project of 12 people finishes in 10 days. How many days for 8 people at the same rate?',
    options: ['12', '15', '16', '20'],
    answer: 1,
  },
  {
    id: 'apt-2',
    kind: 'numerical',
    prompt: 'If 40% of 250 candidates pass a screen, how many fail?',
    options: ['100', '140', '150', '160'],
    answer: 2,
  },
  {
    id: 'apt-3',
    kind: 'logic',
    prompt: 'All mentors are engineers. Some engineers write tests. Which must be true?',
    options: [
      'All mentors write tests',
      'Some mentors might write tests',
      'No engineer is a mentor',
      'Every tester is a mentor',
    ],
    answer: 1,
  },
  {
    id: 'apt-4',
    kind: 'logic',
    prompt: 'Find the next number: 2, 6, 12, 20, 30, ?',
    options: ['36', '40', '42', '48'],
    answer: 2,
  },
  {
    id: 'apt-5',
    kind: 'verbal',
    prompt: 'Choose the word closest in meaning to "concise".',
    options: ['Lengthy', 'Brief', 'Vague', 'Loud'],
    answer: 1,
  },
  {
    id: 'apt-6',
    kind: 'verbal',
    prompt: '"Mitigate" is closest to:',
    options: ['Worsen', 'Ignore', 'Reduce', 'Celebrate'],
    answer: 2,
  },
  {
    id: 'apt-7',
    kind: 'numerical',
    prompt: 'A salary of ₹50,000 rises 10% then falls 10%. The new salary is:',
    options: ['₹50,000', '₹49,500', '₹49,000', '₹45,000'],
    answer: 1,
  },
  {
    id: 'apt-8',
    kind: 'logic',
    prompt: 'If only one statement is true: A says B lies. B says C lies. C says A and B lie. Who tells the truth?',
    options: ['A', 'B', 'C', 'None'],
    answer: 1,
  },
];

export const buildWritten = (questions = [], domains = []) => {
  const fromProfile = (questions || [])
    .map((row) => {
      const q = row.question || row;
      return {
        id: row.id || q.id,
        prompt: q.question_text || q.question || '',
        sample: q.sample_answer || '',
        skill: q.skill || '',
      };
    })
    .filter((row) => row.prompt)
    .slice(0, 2);
  if (fromProfile.length >= 2) return fromProfile;
  const domain = domains[0]?.name || 'this role';
  return [
    ...fromProfile,
    {
      id: 'w-fallback-1',
      prompt: `Describe a problem you solved that is relevant to ${domain}. What did you do, and what changed?`,
      sample: 'Situation, the action you owned, tools used, and a measurable result.',
      skill: domain,
    },
    {
      id: 'w-fallback-2',
      prompt: 'A stakeholder disagrees with your recommendation. How do you respond in writing?',
      sample: 'Acknowledge the concern, restate the goal, offer evidence, propose a next step.',
      skill: 'communication',
    },
  ].slice(0, 2);
};

export const scoreWritten = (answers, items) => {
  const scores = items.map((item, index) => {
    const text = String(answers[index] || '').trim();
    if (text.length < 80) return 40;
    const hay = text.toLowerCase();
    const needles = String(item.sample || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 4);
    const hits = needles.filter((word) => hay.includes(word)).length;
    const coverage = needles.length ? hits / needles.length : 0.5;
    const lengthScore = Math.min(1, text.length / 280);
    return Math.round(100 * (0.45 + 0.35 * coverage + 0.2 * lengthScore));
  });
  const score = Math.round(scores.reduce((a, b) => a + b, 0) / Math.max(scores.length, 1));
  return { score: Math.min(99, score), answers };
};

export const scoreChoices = (picks, items) => {
  let correct = 0;
  items.forEach((item, index) => {
    if (Number(picks[index]) === Number(item.answer)) correct += 1;
  });
  const score = Math.round((correct / Math.max(items.length, 1)) * 100);
  return { score, correct, total: items.length };
};

export const uniqueLatestModeResults = (rows = []) => {
  const ordered = [...(rows || [])].sort((a, b) =>
    String(b.created_at || '').localeCompare(String(a.created_at || '')),
  );
  const seen = new Set();
  const unique = [];
  ordered.forEach((row) => {
    if (!row?.mode || seen.has(row.mode)) return;
    seen.add(row.mode);
    unique.push(row);
  });
  return unique;
};

export const uniqueSuggestionLinks = (links = [], seen) => {
  const used = seen || new Set();
  const unique = [];
  (links || []).forEach((link) => {
    if (!link?.url || used.has(link.url)) return;
    used.add(link.url);
    unique.push(link);
  });
  return unique;
};

/**
 * Prefer WeSenseU analysis cards so PDF/partner prep is not buried under mode rows.
 * Then mode_result, then newest by created_at.
 */
export const prioritizeInterviewReports = (reports = [], limit = 8) => {
  const rows = [...(reports || [])];
  const rank = (source) => {
    if (source === 'wesenseu') return 2;
    if (source === 'mode_result') return 1;
    return 0;
  };
  rows.sort((a, b) => {
    const delta = rank(b?.source) - rank(a?.source);
    if (delta) return delta;
    return String(b.created_at || '').localeCompare(String(a.created_at || ''));
  });
  return rows.slice(0, limit);
};

export const formatStudyDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  if (total < 3600) return `${Math.round(total / 60)}m`;
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};

export const scoreFromResults = (modeId, results = {}) => {
  if (modeId === 'typing') {
    const wpm = Number(results.wpm) || 0;
    const accuracy = Number(results.accuracy) || 0;
    return Math.min(100, Math.round((Math.min(wpm, 80) / 80) * 50 + accuracy * 0.5));
  }
  if (modeId === 'coding') return Math.round(Number(results.score) || 0);
  if (modeId === 'voice' || modeId === 'video') {
    const measured = results.score ?? results.stats?.overallScore;
    if (measured == null || measured === '' || !Number.isFinite(Number(measured))) return null;
    return Math.min(100, Math.max(0, Math.round(Number(measured))));
  }
  if (typeof results.score === 'number') return Math.round(results.score);
  return 0;
};

export const recommendModeFromJd = ({
  title = '',
  content = '',
  skills = [],
  domains = [],
  location = '',
} = {}) => {
  const skillText = (skills || [])
    .map((row) => (typeof row === 'string' ? row : row.name || ''))
    .join(' ');
  const domainText = (domains || [])
    .map((row) => (typeof row === 'string' ? row : row.name || ''))
    .join(' ');
  const blob = [title, content, skillText, domainText, location]
    .join(' ')
    .replace(/<[^>]+>/g, ' ')
    .toLowerCase();
  const hit = (...patterns) => patterns.some((pattern) => pattern.test(blob));

  if (hit(/\b(typing|wpm|words per minute|data entry|transcription|bpo)\b/)) {
    return { mode: 'typing', reason: 'The JD asks for typing speed or high-volume data work.' };
  }
  const codingRole = hit(/\b(engineer|developer|sde|software|backend|frontend|full.?stack|programmer|data scientist|ml engineer)\b/);
  const codingStack = hit(/\b(python|java|javascript|typescript|golang|kotlin|c#|c\+\+|php|ruby|coding|algorithm|leetcode|django|react|sql|docker)\b/);
  if ((codingRole && codingStack) || hit(/\b(data structures?|unit tests?|write code|coding challenge)\b/)) {
    return { mode: 'coding', reason: 'The role needs working code, not only conversation.' };
  }
  if (hit(/\b(aptitude|quantitative|logical reasoning|campus drive|graduate trainee)\b/)) {
    return { mode: 'aptitude', reason: 'Volume or campus screens in this JD start with reasoning.' };
  }
  if (hit(/\b(mcq|multiple choice|objective test)\b/)) {
    return { mode: 'mcq', reason: 'The JD specifies an objective knowledge screen.' };
  }
  if (hit(/\b(case study|policy memo|written communication|analyst|consultant)\b/) && !codingStack) {
    return { mode: 'written', reason: 'The JD emphasises written judgment over code.' };
  }
  if (hit(/\b(video|camera|on-camera|client-facing|client facing|presentation)\b/)) {
    return { mode: 'video', reason: 'The role is camera-facing or presentation-heavy.' };
  }
  if (hit(/\b(phone|telephonic|voice|hr screen|screening call)\b/)) {
    return { mode: 'voice', reason: 'First round looks like a phone screen.' };
  }
  return {
    mode: 'voice',
    reason: 'Start with a phone-screen format. Switch if the JD is more specific.',
  };
};

export const buildCodingContext = (questions = [], skills = [], jd = {}) => {
  const q = (questions || [])[0]?.question || (questions || [])[0] || {};
  const skillRow = (skills || [])[0];
  const skill = (typeof skillRow === 'string' ? skillRow : skillRow?.name) || q.skill || '';
  const prompt = q.question_text || q.question || q.prompt || '';
  const language = preferredCodingLanguage(
    skills,
    `${jd.title || ''} ${jd.content || ''} ${jd.location || ''}`,
  );
  const label = codingLanguageLabel(language);
  return {
    preferredLanguage: language,
    skillHint: skill,
    rolePrompt: prompt,
    intro: prompt
      ? `Pass the tests in ${label}. Hiring teams using ${skill || 'this stack'} will also ask: ${prompt}`
      : `Pass the tests in ${label}. Score is tests passed, not conversation.`,
  };
};
